/**
 * C.8 — IndexedDB warm cache for the vault search index.
 *
 * Per memory rule feedback_local_edit_queue.md:
 *   - never await between IDB requests in the same transaction
 *   - always close the DB in oncomplete / onerror / onabort
 *
 * Schema is a single `vaultIndex` object store keyed by clientSlug.
 * Each row carries the serialized SearchableNote[] + Fuse JSON index +
 * a content checksum that lets the caller decide whether to invalidate.
 */

import type { SearchableNote } from './searchIndex';

const DB_NAME = 'repancy-vault-cache';
const DB_VERSION = 1;
const STORE = 'vaultIndex';

export interface CachedIndex {
  clientSlug: string;
  /** Stable checksum of the notes the index was built from. */
  checksum: string;
  items: SearchableNote[];
  /** Fuse JSON form (toJSON() output). */
  fuseIndex: unknown;
  savedAt: number;
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'clientSlug' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
}

export async function loadCachedIndex(
  clientSlug: string,
): Promise<CachedIndex | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise<CachedIndex | null>((resolve) => {
    let result: CachedIndex | null = null;
    const tx = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    const req = store.get(clientSlug);
    req.onsuccess = () => {
      const row = req.result as CachedIndex | undefined;
      if (row) result = row;
    };
    req.onerror = () => {
      /* swallow; resolve with current `result` (null) in oncomplete */
    };
    tx.oncomplete = () => {
      db.close();
      resolve(result);
    };
    tx.onerror = () => {
      db.close();
      resolve(null);
    };
    tx.onabort = () => {
      db.close();
      resolve(null);
    };
  });
}

export async function saveCachedIndex(row: CachedIndex): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    store.put(row);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      resolve();
    };
    tx.onabort = () => {
      db.close();
      resolve();
    };
  });
}

export async function clearCachedIndex(clientSlug: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  return new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    store.delete(clientSlug);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      resolve();
    };
    tx.onabort = () => {
      db.close();
      resolve();
    };
  });
}

/**
 * Deterministic checksum of a parsed-notes list. We hash `path:etag`
 * pairs (where etag falls back to the body length when undefined) so
 * any rename / content change invalidates the cache.
 *
 * Cheap FNV-1a over a sorted concatenated string. Good enough for a
 * cache key; we don't need cryptographic strength.
 */
export function notesChecksum(
  rows: { path: string; etagOrLen: string | number }[],
): string {
  const sorted = [...rows].sort((a, b) => a.path.localeCompare(b.path));
  const joined = sorted.map((r) => `${r.path}|${r.etagOrLen}`).join('\n');
  let h = 0x811c9dc5;
  for (let i = 0; i < joined.length; i++) {
    h ^= joined.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}
